/**
 * API Client
 * Axios-based HTTP client with authentication, silent token refresh, and error handling
 */

import { mfaRequirement } from './mfa-required';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { API_CONFIG, API_ENDPOINTS } from './api-config';
import toast from 'react-hot-toast';
import { isPolicyDenial, policyDenialMessage } from '@/lib/policy-window';
import { useAuthStore, currentCsrfToken } from '@/store/auth-store';
import { withReadCache } from '@/lib/api-read-cache';
import { classifyApiError, isSafeToReplay, isSessionFailure, requestIdOf } from '@/lib/api-error';
import { announceSessionExpired, awaitSessionRecovery } from '@/lib/session-recovery';
import { logger } from '@/lib/logger';

// Allow any call site to opt a request out of the global 402/403/429 error
// toast — used by background/polling requests and by callers that already
// surface the error themselves (prevents double-notifying).
declare module 'axios' {
  export interface AxiosRequestConfig {
    /** When true, the response interceptor won't fire a global error toast. */
    skipGlobalErrorToast?: boolean;
  }
}

/**
 * Extract a human-readable error message from an axios error response.
 *
 * Backend errors arrive in several shapes — all need to render as a clean
 * single line, never "[object Object]" or "Request failed with status 4xx":
 *
 *   { detail: "Some text" }
 *   { detail: { message: "...", error: "...", ... } }    ← e.g. tier_limit_exceeded
 *   { detail: [{ msg: "...", loc: [...], type: "..." }] } ← FastAPI validation
 *   { message: "..." }                                    ← legacy / non-FastAPI shape
 *
 * Falls back to a friendly default rather than the raw axios message.
 */
/**
 * A scoped-policy refusal (backend services/policies/violations.py) carries
 * {code: 'policy_violation', policy, policy_name, message}. It is shown the
 * same way everywhere: "Policy violation — <policy>: <what was refused>".
 */
export function policyViolationMessage(detail: any): string | null {
  if (!detail || typeof detail !== 'object' || detail.code !== 'policy_violation') return null;
  return `Policy violation — ${detail.policy_name || detail.policy}: ${detail.message || 'this action is not allowed.'}`;
}

export function extractApiErrorMessage(error: any, fallback = 'Something went wrong. Please try again.'): string {
  // Already-normalised plain string
  if (typeof error === 'string') return error;
  const message = extractApiErrorMessageInner(error, fallback);
  // F23: a server error names a correlation id ("...quote the correlation_id
  // below") in the body or the X-Request-ID header, but only `detail` was ever
  // rendered, so the id the message pointed at was never on screen.
  const status = error?.response?.status;
  if (typeof status === 'number' && status >= 500) {
    const rid = requestIdOf(error);
    if (rid && !message.includes(rid)) {
      const text = message.replace(/\s*Contact support with the correlation_id below\.?/i, '').trim();
      return `${text} Reference: ${rid}`;
    }
  }
  return message;
}

function extractApiErrorMessageInner(error: any, fallback: string): string {

  // Axios error with a response body
  const data = error?.response?.data;
  if (data) {
    // Most common: {detail: ...}
    const detail = data.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object') {
      // A Layer 3 policy denial carries the window that blocked it. Handled
      // BEFORE the generic `detail.message` branch, which would otherwise
      // return "Blocked by policy: …" and drop the only part the user can act
      // on — when they may try again, in their own timezone rather than the
      // policy's UTC.
      const violation = policyViolationMessage(detail);
      if (violation) return violation;
      if (isPolicyDenial(detail)) return policyDenialMessage(detail);
      // Single object with a 'message' (or 'error', or 'msg') field
      if (typeof detail.message === 'string') return detail.message;
      if (typeof detail.error === 'string')   return detail.error;
      if (typeof detail.msg === 'string')     return detail.msg;
      // FastAPI validation: array of {loc, msg, type}
      if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0];
        if (typeof first?.msg === 'string') {
          const field = Array.isArray(first.loc) ? first.loc.slice(-1)[0] : '';
          return field ? `${field}: ${first.msg}` : first.msg;
        }
      }
    }
    // Some endpoints put the message at the top level instead
    if (typeof data.message === 'string') return data.message;
    if (typeof data.error === 'string')   return data.error;
  }

  // Network / CORS / timeout — axios's own message is at least informative
  if (error?.code === 'ECONNABORTED') return 'Request timed out. Please try again.';
  if (error?.message && typeof error.message === 'string' && !error.message.startsWith('Request failed with')) {
    return error.message;
  }
  return fallback;
}

/**
 * The API client's own error toast (permission / quota / concurrency). It uses
 * a fixed id so <ToastPolicy> (components/providers/ToastPolicy.tsx) can keep
 * this specific message and drop a page's generic "Failed to …" for the same
 * failure. Position and styling come from the single <Toaster> in layout.tsx.
 */
export const API_ERROR_TOAST_ID = 'uzo-api-error';

export const showErrorToast = (message: string) => {
  if (typeof window === 'undefined') return;
  toast.error(message, { id: API_ERROR_TOAST_ID });
};

/**
 * De-duplicated error toast — suppresses an identical (status + message) toast
 * fired again within a short window, so a burst of failing requests (or two
 * mutations racing) shows the message once instead of stacking N copies.
 */
const TOAST_DEDUP_MS = 3000;
const recentToasts = new Map<string, number>();
const showErrorToastDeduped = (message: string, status: number) => {
  const now = Date.now();
  const key = `${status}:${message}`;
  const last = recentToasts.get(key);
  if (last && now - last < TOAST_DEDUP_MS) return;
  recentToasts.set(key, now);
  // Prune stale entries so the map can't grow unbounded.
  for (const [k, t] of recentToasts) {
    if (now - t >= TOAST_DEDUP_MS) recentToasts.delete(k);
  }
  showErrorToast(message);
};

/**
 * Is a backend message safe to show verbatim, or is it a machine code
 * ("permission_denied", "Forbidden") / the generic fallback? We only surface
 * backend text when it reads like a real sentence, otherwise use our copy.
 */
const isUserFriendlyMessage = (msg: string): boolean => {
  if (!msg) return false;
  if (msg === 'Something went wrong. Please try again.') return false;
  if (msg === 'Forbidden' || msg === 'Unauthorized') return false;
  if (/^[a-z0-9_]+$/.test(msg)) return false; // snake_case machine code
  return msg.includes(' ') || msg.length > 12;
};

/**
 * Build the 402 (tier_limit_exceeded) message, naming the resource/limit when
 * the response body carries it.
 */
const buildQuotaMessage = (error: any, friendly: string): string => {
  // If the backend already returned a friendly limit sentence, trust it.
  if (isUserFriendlyMessage(friendly) && friendly.toLowerCase().includes('limit')) {
    return friendly;
  }
  const data = error?.response?.data;
  const detail = data?.detail;
  const src = detail && typeof detail === 'object' && !Array.isArray(detail) ? detail : data || {};
  const resource = src.resource || src.resource_type || src.entity;
  const limit = src.limit ?? src.max ?? src.cap;
  let msg = "You've reached your plan's limit";
  if (resource) msg += ` for ${String(resource).replace(/_/g, ' ')}`;
  msg += '.';
  if (limit != null && limit !== '') msg += ` (max ${limit})`;
  return msg;
};

/**
 * SECURITY: Token management removed - using HttpOnly cookies only
 * Tokens are no longer stored in localStorage to prevent XSS attacks
 * Authentication is handled via secure HttpOnly cookies set by the backend
 */

/**
 * Create axios instance with default configuration
 */
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_CONFIG.baseURL,
    timeout: API_CONFIG.timeout,
    headers: API_CONFIG.headers,
    withCredentials: true, // CRITICAL: Send HTTP-only cookies with requests
  });

  // Share identical in-flight GETs and reuse recent reads when the user
  // navigates between pages. Polls, Refresh clicks and writes always hit the
  // network — see api-read-cache.ts for the rules.
  client.defaults.adapter = withReadCache(axios.getAdapter(axios.defaults.adapter), () => {
    const u = useAuthStore.getState().user as { user_id?: string; tenant_id?: string } | null;
    return `${u?.tenant_id ?? '-'}:${u?.user_id ?? '-'}`;
  });

  // Request interceptor - Add cache-prevention headers, CSRF token, and logging
  client.interceptors.request.use(
    (config) => {
      // Prevent browser and proxy caching for all API requests
      config.headers = config.headers || {};
      config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      config.headers['Pragma'] = 'no-cache';
      config.headers['Expires'] = '0';

      // Client metadata for audit logging
      if (typeof window !== 'undefined') {
        try {
          config.headers['X-Client-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
          config.headers['X-Client-Language'] = navigator.language;
        } catch { /* ignore */ }
      }

      // Add CSRF token for state-changing requests (POST, PUT, PATCH, DELETE)
      const method = config.method?.toUpperCase();
      if (method && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
        const csrfToken = currentCsrfToken();
        if (csrfToken) {
          config.headers['X-CSRF-Token'] = csrfToken;
        }
      }

      return config;
    },
    (error) => {
      logger.error('[API Client] Request interceptor error:', error);
      return Promise.reject(error);
    }
  );

  // =========================================================================
  // Silent Token Refresh Logic
  // =========================================================================
  // When access_token expires (401), we attempt POST /auth/refresh which uses
  // the refresh_token HttpOnly cookie (7-day lifetime) to get a new
  // access_token. Only if refresh also fails do we redirect to login.
  // =========================================================================

  let isRefreshing = false;
  let refreshSubscribers: Array<(success: boolean) => void> = [];

  /**
   * Queue failed requests while a refresh is in-flight.
   * Once refresh completes, all queued requests retry or reject.
   */
  const subscribeToRefresh = (callback: (success: boolean) => void) => {
    refreshSubscribers.push(callback);
  };

  const onRefreshComplete = (success: boolean) => {
    refreshSubscribers.forEach(cb => cb(success));
    refreshSubscribers = [];
  };

  /**
   * Attempt silent token refresh via HttpOnly refresh_token cookie.
   * Returns true if refresh succeeded, false otherwise.
   */
  const attemptSilentRefresh = async (): Promise<boolean> => {
    try {
      // POST /auth/refresh is CSRF-enforced (it is NOT a staged product path and
      // never was exempt). This call uses a raw axios instance to avoid the
      // response-interceptor recursion, so it bypasses the request interceptor
      // that normally attaches X-CSRF-Token — we must attach it here or the
      // refresh 403s and every expired-token request becomes "Session expired"
      // (notably the long Craft build flow, which crosses the 60-min token life).
      const csrfToken = currentCsrfToken();
      const refreshResponse = await axios.post(
        `${API_CONFIG.baseURL}${API_ENDPOINTS.auth.refresh}`,
        {},
        {
          withCredentials: true,
          timeout: 10000,
          headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : undefined,
        }
      );

      if (refreshResponse.status === 200) {
        logger.debug('[API Client] Silent token refresh succeeded');

        // Update session expiry in auth store (new access token = fresh 60min)
        const authState = useAuthStore.getState();
        if (authState.isAuthenticated) {
          const newExpiresAt = new Date(Date.now() + 60 * 60 * 1000).toISOString(); // 60 min
          useAuthStore.setState({ sessionExpiresAt: newExpiresAt, lastActivity: Date.now() });
        }

        return true;
      }
      return false;
    } catch (err) {
      logger.warn('[API Client] Silent refresh failed:', (err as any)?.response?.status || (err as any)?.message);
      return false;
    }
  };

  type RecoverableRequest = AxiosRequestConfig & { _retry?: boolean; _sessionRecovered?: boolean };

  /**
   * F19 — after a session failure, decide what happens to the request.
   *
   * - Session renewed and the request is safe to replay (GET/PUT/DELETE, or a
   *   POST/PATCH carrying an Idempotency-Key): replay it once, silently.
   * - Anything else is never replayed blindly. A write waits, pending, while the
   *   session dialog asks the user to sign in again (new tab) or confirm the
   *   resend; the page is not navigated away, so the draft is kept. Retry sends it
   *   once more with the renewed cookies and the CSRF token from the new login.
   * - A read is rejected at once (polls will pick up again) and the dialog opens.
   */
  const recoverAfterSessionFailure = async (
    originalRequest: RecoverableRequest,
    error: AxiosError,
    refreshed: boolean,
  ) => {
    const method = (originalRequest.method || 'get').toUpperCase();
    originalRequest._retry = true;
    if (refreshed && isSafeToReplay(method, originalRequest.headers)) {
      originalRequest._sessionRecovered = true;
      return client(originalRequest);
    }
    if (typeof window === 'undefined') return Promise.reject(error);
    if (method === 'GET' || method === 'HEAD') {
      announceSessionExpired();
      return Promise.reject(error);
    }
    try {
      await awaitSessionRecovery({ renewed: refreshed });
    } catch {
      return Promise.reject(error);
    }
    // A sign-in in another tab rewrote the persisted auth state (new CSRF token).
    try { await (useAuthStore as any).persist?.rehydrate?.(); } catch { /* keep current state */ }
    originalRequest._sessionRecovered = true;
    return client(originalRequest);
  };

  // Response interceptor - Handle 401 with silent refresh
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as RecoverableRequest;

      // =====================================================================
      // Session failures — 401, or the CSRF middleware's 403 for a missing or
      // unknown session ("Session expired. Please log in again."). Attempt one
      // silent refresh, then replay only what is safe to replay (F19).
      // Permission 403s (code permission_denied) are NOT session failures.
      // =====================================================================
      const failedStatus = error.response?.status;
      if (
        originalRequest
        && !originalRequest._sessionRecovered
        && isSessionFailure(failedStatus, error.response?.data)
      ) {
        // Don't refresh for public pages or the refresh endpoint itself
        if (typeof window !== 'undefined') {
          const currentPath = window.location.pathname;
          const isPublicPage = currentPath === '/' || ['/login', '/register', '/forgot-password', '/docs'].some(
            route => currentPath.startsWith(route)
          );
          if (isPublicPage) return Promise.reject(error);
        }

        // Don't try to refresh the refresh endpoint itself (infinite loop prevention)
        const requestUrl = originalRequest.url || '';
        if (requestUrl.includes('/auth/refresh') || requestUrl.includes('/auth/login')) {
          return Promise.reject(error);
        }

        // If a refresh is already in-flight, queue this request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            subscribeToRefresh((success: boolean) => {
              recoverAfterSessionFailure(originalRequest, error, success).then(resolve, reject);
            });
          });
        }

        // Start the refresh (at most once per request)
        let refreshed = false;
        if (!originalRequest._retry) {
          isRefreshing = true;
          originalRequest._retry = true;
          refreshed = await attemptSilentRefresh();
          isRefreshing = false;
          onRefreshComplete(refreshed);
        }

        return recoverAfterSessionFailure(originalRequest, error, refreshed);
      }

      // Normalise the error message on every failed response so call sites
      // that do `toast.error(err.message)` get the human text from the API,
      // not "Request failed with status code 4xx" or "[object Object]".
      const friendly = extractApiErrorMessage(error);
      const status = error.response?.status;

      // =====================================================================
      // Centralised, actionable toasts for the Sprint 3 mutation-gating codes
      // (403 permission_denied, 402 tier_limit_exceeded, 429 concurrency cap).
      // 401 is handled above; 400 / 404 / 5xx stay silent here — those call
      // sites already `toast.error(err.message)` and get the friendly text from
      // the message overwrite below, so auto-toasting would duplicate.
      // Callers that handle the error themselves (or background polls) opt out
      // with `skipGlobalErrorToast: true`; identical toasts are de-duped.
      // =====================================================================
      const skipToast = (originalRequest as AxiosRequestConfig)?.skipGlobalErrorToast === true;
      const isViolation = !!policyViolationMessage((error.response?.data as any)?.detail);
      if (isViolation && typeof window !== 'undefined') {
        // A policy violation is ALWAYS announced — whatever the status (403, 413,
        // 423, 429, …) and even when the caller handles the error itself — so the
        // person sees which policy stopped them. One toast, de-duplicated.
        showErrorToastDeduped(friendly, status ?? 0);
      } else if (!skipToast && typeof window !== 'undefined') {
        const mfa = status === 403 ? mfaRequirement((error.response?.data as any)?.detail) : null;
        if (mfa) {
          // IAM-04: an admin action needs a second factor in this session.
          showErrorToastDeduped(mfa.message, 403);
          if (mfa.kind === 'enroll') {
            window.location.assign(mfa.redirect);
          } else {
            window.dispatchEvent(new CustomEvent('uzo:step-up-required', { detail: { permission: mfa.permission } }));
          }
        } else if (status === 403) {
          // F23: name the missing permission when the server says which one.
          const denied = classifyApiError(error);
          showErrorToastDeduped(
            denied.kind === 'permission_denied' && denied.permission
              ? `${denied.message} ${denied.remediation}`
              : isUserFriendlyMessage(friendly) ? friendly : "You don't have permission to do that.",
            403,
          );
        } else if (status === 402) {
          showErrorToastDeduped(buildQuotaMessage(error, friendly), 402);
        } else if (status === 429) {
          showErrorToastDeduped(
            isUserFriendlyMessage(friendly) ? friendly : 'Too many concurrent runs — try again shortly.',
            429,
          );
        }
      }

      // Overwrite axios's generic message so existing call sites that read
      // `err.message` (~50 of them) automatically render the friendly text.
      try {
        Object.defineProperty(error, 'message', { value: friendly, writable: true, configurable: true });
      } catch { /* ignore */ }

      return Promise.reject(error);
    }
  );

  return client;
};

// Create singleton instance
export const apiClient = createApiClient();

/**
 * DEPRECATED: Token management functions removed
 * Authentication now uses secure HttpOnly cookies only
 * Tokens are never exposed to JavaScript to prevent XSS attacks
 */

/**
 * Generic API request wrapper with typed response
 */
export async function apiRequest<T>(
  config: AxiosRequestConfig
): Promise<T> {
  try {
    const response: AxiosResponse<T> = await apiClient(config);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      // This is the single busiest error path in the app — ~500 call sites
      // reach it. It used to rebuild the message itself:
      //
      //   data?.message || data?.detail || axiosError.message || '...'
      //
      // On a 422 FastAPI returns `detail` as an ARRAY of {loc,msg,type}. An
      // array is truthy, so it won the ||-chain and `new Error(array)` produced
      // the literal string "[object Object]" — which is what the login page
      // showed users ("Login Failed [object Object]", observed 2026-09-19).
      //
      // extractApiErrorMessage already understands every shape this backend
      // returns, including that array, and the response interceptor above has
      // normally already put the same text on `error.message`.
      const wrapped = new Error(extractApiErrorMessage(error, 'An error occurred')) as Error & { status?: number; code?: string; detail?: unknown };
      // Keep the HTTP status and axios code: callers tell a 404 from a
      // gateway error (backend restarting) by these, not by the text.
      wrapped.status = error.response?.status;
      wrapped.code = error.code;
      // Structured refusals (e.g. 409 {code:'judges_uncalibrated', …}) carry
      // a machine code the caller branches on — keep the raw FastAPI detail.
      wrapped.detail = (error.response?.data as any)?.detail;
      throw wrapped;
    }
    throw error;
  }
}

export default apiClient;
